import { 
  type IThemeProvider, 
  SciChartJSDarkv2Theme,
  SciChartJSLightTheme
} from 'scichart'

/**
 * Custom SciChart theme that matches MUI's default dark theme
 * Extends the built-in dark theme and overrides key colors
 */
export class MUICompatibleDarkTheme extends SciChartJSDarkv2Theme {
  // MUI Dark Theme Colors
  static readonly BACKGROUND_PAPER = '#1e1e1e'
  static readonly TEXT_PRIMARY = '#ffffff'
  static readonly TEXT_SECONDARY = 'rgba(255, 255, 255, 0.7)'
  static readonly DIVIDER = 'rgba(255, 255, 255, 0.12)'
  static readonly PRIMARY_MAIN = '#90caf9'
  static readonly GRID_COLOR = 'rgba(255, 255, 255, 0.08)'

  constructor() {
    super()
    
    // Override chart background to match MUI paper
    this.sciChartBackground = MUICompatibleDarkTheme.BACKGROUND_PAPER
    
    // Update text colors to match MUI
    this.axisTitleColor = MUICompatibleDarkTheme.TEXT_PRIMARY
    this.tickTextBrush = MUICompatibleDarkTheme.TEXT_SECONDARY
    this.textAnnotationForeground = MUICompatibleDarkTheme.TEXT_PRIMARY
    
    // Update grid colors to be more subtle like MUI
    this.majorGridLineBrush = MUICompatibleDarkTheme.GRID_COLOR
    this.minorGridLineBrush = 'rgba(255, 255, 255, 0.04)'
    
    // Update accent colors to use MUI primary
    this.rolloverLineBrush = MUICompatibleDarkTheme.PRIMARY_MAIN
    this.cursorLineBrush = MUICompatibleDarkTheme.PRIMARY_MAIN
    this.loadingAnimationForeground = MUICompatibleDarkTheme.PRIMARY_MAIN
    
    // Update selection colors
    this.rubberBandStrokeBrush = MUICompatibleDarkTheme.PRIMARY_MAIN
    this.rubberBandFillBrush = 'rgba(144, 202, 249, 0.2)'
  }
}

/**
 * Create the appropriate theme based on the theme parameter
 */
export const createWaveChartTheme = (theme: 'dark' | 'light'): IThemeProvider => {
  if (theme === 'light') {
    return new SciChartJSLightTheme()
  }
  
  // Use our custom MUI-compatible dark theme
  return new MUICompatibleDarkTheme()
}

/**
 * MUI-compatible color palette for curves
 * These colors work well on dark backgrounds and follow MUI's color philosophy
 */
export const MUI_CHART_COLORS = [
  '#90caf9', // Primary blue (matches MUI primary)
  '#f48fb1', // Secondary pink (matches MUI secondary)
  '#66bb6a', // Success green
  '#ffa726', // Warning orange
  '#f44336', // Error red
  '#29b6f6', // Info blue
  '#ab47bc', // Purple
  '#26c6da', // Cyan
  '#d4e157', // Lime
  '#ff7043', // Deep orange
] as const

export type MUIChartColor = typeof MUI_CHART_COLORS[number] 